import moment from 'moment';

import { ITimesheetCellDTO, ITimesheetDTO, ITimesheetRowDTO, ITimesheetUpdateRequestDTO } from 'src/api/dtos';

import { TimesheetGrid, TimesheetGridRow } from './TimesheetGrid.types';

export const TIMESHEET_MIN_ROW_COUNT = 5;

export const TIMESHEET_ROW_HEIGHT = 48;

export const TIMESHEET_LEFT_COLUMNS_MIN_WIDTH = 140;

export const TIMESHEET_DATE_COLUMN_MIN_WIDTH = 120;

export const TIMESHEET_RIGHT_COLUMNS_MIN_WIDTH = 90;

export const TIMESHEET_DATE_HEADER = 'timesheet-date-header';

export const TIMESHEET_DATE_CELL = 'timesheet-date-cell';

export const TIMESHEET_SELECTED_CELL = 'timesheet-selected-cell';

export const TIMESHEET_CELL_MENU_ICON = 'timesheet-cell-menu-icon';

export const TIMESHEET_DISABLED_GRID = 'timesheeet-disabled-grid';

export const TIMESHEET_DISABLED_ROW = 'timesheeet-disabled-row';

export const TIMESHEET_DISABLED_CELL = 'timesheeet-disabled-cell';

const TIMESHEET_MINIMUM_ROWS_COUNT = 5;

const isTimesheetCellEmpty = (cell: ITimesheetCellDTO) => {
  return !cell.expenses.length && !cell.duration && !cell.note;
};

export const isTimesheetRowEmpty = (row: TimesheetGridRow | ITimesheetRowDTO) => {
  return (
    !row.department &&
    row.jobs.every((job) => !job?.values.length) &&
    !row.absenceAndOverride &&
    row.cells.every((cell) => isTimesheetCellEmpty(cell))
  );
};

const buildTimesheetEmptyRow = (dates: string[]): ITimesheetRowDTO => ({
  department: null,
  jobs: [],
  absenceAndOverride: null,
  cells: dates.map((date) => ({
    date,
    expenses: [],
    duration: 0,
    isReadOnly: false,
    note: null,
  })),
  totalDurationInSeconds: 0,
  totalExpenses: 0,
  isScheduledAbsence: false,
});

/**
 * Modify ITimesheetDTO from API to add id to each row; needed to uniquely identify each row
 *  - Add 'id' property as index to each ITimesheetRowDTO
 */
export const buildTimesheetGrid = (timesheet: ITimesheetDTO): TimesheetGrid => {
  const dates = timesheet.columns.map((column) => column.date);
  const rowsCount = timesheet.rows.length;

  /**
   * If timesheet rows count is less than TIMESHEET_MINIMUM_ROWS_COUNT
   * - Add (TIMESHEET_MINIMUM_ROWS_COUNT - rowsCount) empty rows
   * Otherwise
   * - Add one empty row
   */
  const rows = [
    ...timesheet.rows,
    ...(rowsCount >= TIMESHEET_MINIMUM_ROWS_COUNT
      ? [buildTimesheetEmptyRow(dates)]
      : Array<ITimesheetRowDTO>(TIMESHEET_MINIMUM_ROWS_COUNT - rowsCount).fill(buildTimesheetEmptyRow(dates))),
  ];

  return {
    ...timesheet,
    rows: [...rows.map((row, index) => ({ ...row, id: index }))],
  };
};

/**
 * Grid is disabled/read only if
 * - 'isReadOnly' flag is true
 * - 'status' is 'APPROVED'
 */
export const isTimesheetDisabled = (timesheet: ITimesheetDTO | TimesheetGrid) => {
  if (timesheet.isReadOnly) {
    return true;
  }

  return false;
};

/**
 * Row is disabled/read only if
 * - 'isScheduledAbsence' flag is true
 */
export const isTimesheetRowDisabled = (row: ITimesheetRowDTO | TimesheetGridRow) => {
  if (row.isScheduledAbsence) {
    return true;
  }

  return false;
};

/**
 * Cell is disabled/read only if
 * - 'isReadOnly' flag is true
 * - 'date' is same or before timesheet 'lockDate'
 */
export const isTimesheetCellDisabled = (cell: ITimesheetCellDTO, lockDate: string) => {
  if (cell.isReadOnly) {
    return true;
  }

  if (moment(cell.date).isSameOrBefore(lockDate)) {
    return true;
  }

  return false;
};

export const isValidUUID = (value: string | null) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value || '');

export const buildTimesheetUpdate = (
  startDate: string,
  endDate: string,
  timesheet: TimesheetGrid,
): ITimesheetUpdateRequestDTO => {
  return {
    startDate,
    endDate,
    updates: timesheet.rows
      .filter((row) => !isTimesheetRowEmpty(row) && !row.isScheduledAbsence) // Do not include rows with isScheduledAbsence true
      .map((row) => {
        return {
          departmentId: row.department?.id,
          jobs: row.jobs.map((job) => job?.values[0]?.value ?? null),
          timeCategoryId: row.absenceAndOverride?.id,
          items: row.cells.map((cell) => {
            return {
              date: cell.date,
              duration: cell.duration,
              note: cell.note,
              expenses: cell.expenses.map((expense) => {
                return {
                  timeCategoryId: expense.id,
                  amount: expense.memoAmount,
                  note: expense.note,
                  referenceId: expense.attachment?.referenceId ?? null,
                };
              }),
            };
          }),
        };
      }),
  };
};
